import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { UserRound, Phone, MessageCircle, MapPin, Clock, Instagram, Navigation, Loader2, CheckCircle2, ExternalLink, Mail, Star } from "lucide-react";
import { api, errMsg, useSeo, waLink, fmtDate, POST_CATS, safeUrl } from "../lib/api";
import { useSite } from "../context/AppContext";
import { PageHero, Section, Loading, Empty, Text, Badge } from "../components/Common";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

export function Corporate() {
  const s = useSite();
  const f = s.founder || {};
  useSeo("Kurumsal", "Ada Özel Güvenlik Eğitim Kurumu hakkında: misyon, vizyon, eğitim anlayışı ve kurucumuz.");
  const blocks = [["Hakkımızda", s.about, "about"], ["Misyonumuz", s.mission, "mission"], ["Vizyonumuz", s.vision, "vision"], ["Eğitim anlayışımız", s.approach, "approach"]];
  return (
    <>
      <PageHero eyebrow="Kurumsal" title="Ada Özel Güvenlik Eğitim Kurumu" text="Kuşadası / Aydın" />
      <Section>
        <div className="grid gap-4 md:grid-cols-2">{blocks.map(([t, v, id]) => <div key={t} className="card-ada p-6 sm:p-8" data-testid={`corp-${id}`}><h2 className="font-black text-xl mb-3">{t}</h2><Text value={v} fallback="[Eklenecek]" /></div>)}</div>
      </Section>
      <Section eyebrow="Kurucumuz" title={f.name || "Tolga Ünal"} className="!pt-0">
        <div className="grid gap-6 md:grid-cols-[220px_1fr] card-ada p-6 sm:p-8" data-testid="corp-founder">
          <div className="aspect-[4/5] bg-zinc-100 flex items-center justify-center">{f.photo ? <img src={f.photo} alt={f.name} className="w-full h-full object-cover" /> : <UserRound size={48} className="text-zinc-300" />}</div>
          <div><p className="font-bold text-[#dc2626]" data-testid="founder-title">{f.title}</p>{f.tagline && <p className="font-display font-black text-xl mt-1">{f.tagline}</p>}<div className="mt-3"><Text value={f.bio} fallback="[Özgeçmiş eklenecek]" /></div></div>
        </div>
        {!!f.journey?.length && (
          <div className="mt-8" data-testid="founder-journey"><h3 className="font-black text-xl mb-4">Girişimcilik Yolculuğum</h3>
            <ol className="grid gap-3 md:grid-cols-2">{f.journey.map(([y, t, d]) => <li key={y + t} className="card-ada p-5 flex gap-4"><span className="font-display font-black text-2xl text-[#dc2626]">{y}</span><span><b>{t}</b><span className="block text-sm text-zinc-600 mt-1">{d}</span></span></li>)}</ol></div>
        )}
        <div className="mt-8 grid gap-4 md:grid-cols-3">
          {[["Yönetim Anlayışım", f.management], ["Hayat Felsefem", f.life], ["Vizyonum", f.purpose]].filter(([, v]) => v).map(([t, v]) => <div key={t} className="card-ada p-6"><h3 className="font-black text-lg mb-3">{t}</h3><Text value={v} fallback="" /></div>)}
        </div>
        <div className="mt-6 flex gap-3 flex-wrap"><Link to="/egitmenlerimiz" className="btn-dark">Eğitmen Kadromuz</Link><Link to="/egitimlerimiz" className="btn-red">Eğitimlerimiz</Link><Link to="/iletisim" className="btn-dark">İletişim ve Ulaşım</Link></div>
      </Section>
    </>
  );
}

export function Instructors() {
  const [items, setItems] = useState(null);
  useSeo("Eğitmenlerimiz", "Ada Özel Güvenlik Eğitim Kurumu kurucu ve eğitmen kadrosu.");
  useEffect(() => { api.get("/instructors").then((r) => setItems(r.data)); }, []);
  return (
    <>
      <PageHero eyebrow="Kadro" title="Kurucumuz ve Eğitmenlerimiz" text="Eğitmen bilgileri kişilerin onayıyla ve belgelenmiş bilgilerle yayınlanır." />
      <Section>
        {!items ? <Loading /> : items.length ? (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((p) => (
              <article key={p.id} className="card-ada" data-testid={`instructor-${p.id}`}>
                <div className="aspect-[4/3] bg-zinc-100 flex items-center justify-center">{p.photo ? <img src={p.photo} alt={p.name} loading="lazy" className="w-full h-full object-cover" /> : <UserRound size={48} className="text-zinc-300" />}</div>
                <div className="p-6">
                  {p.is_founder && <Badge tone="red">Kurucu</Badge>}
                  <h2 className="font-black text-xl mt-2">{p.name}</h2>
                  <p className="text-sm text-zinc-500">{p.role}</p>
                  <div className="mt-3 text-sm"><Text value={p.expertise} fallback="" /><Text value={p.experience} fallback="" /><Text value={p.bio} fallback="" /></div>
                  {!!p.authorized_areas?.length && <div className="flex flex-wrap gap-1.5 mt-2">{p.authorized_areas.map((a) => <Badge key={a}>{a}</Badge>)}</div>}
                </div>
              </article>
            ))}
          </div>
        ) : <Empty text="Eğitmen bilgileri yakında eklenecek." />}
      </Section>
    </>
  );
}

export function Faq() {
  const [items, setItems] = useState(null);
  useSeo("Sıkça Sorulan Sorular", "Özel güvenlik kursu başvurusu, ücretler, sınavlar ve eğitimler hakkında sıkça sorulan sorular.");
  useEffect(() => { api.get("/faqs").then((r) => setItems(r.data)); }, []);
  return (
    <>
      <PageHero eyebrow="SSS" title="Sıkça Sorulan Sorular" />
      <Section className="max-w-4xl">
        {!items ? <Loading /> : <Accordion type="single" collapsible className="card-ada px-6" data-testid="faq-list">{items.map((f) => <AccordionItem key={f.id} value={f.id}><AccordionTrigger className="text-left font-bold" data-testid={`faq-q-${f.id}`}>{f.question}</AccordionTrigger><AccordionContent className="text-zinc-700">{f.answer}</AccordionContent></AccordionItem>)}</Accordion>}
      </Section>
    </>
  );
}

export function Posts({ kind }) {
  const base = kind === "guide" ? "/rehber" : "/duyurular";
  const [items, setItems] = useState(null);
  const [cat, setCat] = useState("");
  const cats = kind === "guide" ? ["mevzuat", "meslek"] : ["kurs_kayit", "sinav", "programlar", "mevzuat_degisiklik", "bilgilendirme", "kurumsal"];
  useSeo(kind === "guide" ? "Özel Güvenlik Mevzuatı ve Rehber" : "Duyurular ve Haberler", kind === "guide" ? "Özel güvenlik mevzuatı ve meslek rehberi yazıları." : "Kurs kayıt, sınav ve kurumsal duyurular.");
  useEffect(() => { setItems(null); api.get("/posts", { params: { kind, category: cat } }).then((r) => setItems(r.data)); }, [kind, cat]);
  return (
    <>
      <PageHero eyebrow={kind === "guide" ? "Rehber" : "Duyurular"} title={kind === "guide" ? "Özel Güvenlik Mevzuatı ve Rehber" : "Duyurular ve Haberler"} text={kind === "guide" ? "Kurum bilgilendirmeleri resmî kaynaklardan ayrı işaretlenir; mevzuat yazılarında kaynak ve kontrol tarihi belirtilir." : ""} />
      <Section>
        <div className="flex gap-2 overflow-x-auto pb-3 mb-6">{["", ...cats].map((k) => <button key={k} onClick={() => setCat(k)} data-testid={`post-cat-${k || "all"}`} className={`shrink-0 px-4 py-2 text-xs font-bold uppercase border ${cat === k ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300"}`}>{k ? POST_CATS[k] : "Tümü"}</button>)}</div>
        {!items ? <Loading /> : items.length ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {items.map((p) => (
              <Link key={p.id} to={`${base}/${p.slug}`} className="card-ada block" data-testid={`post-card-${p.slug}`}>
                {p.cover && <img src={p.cover} alt={p.title} loading="lazy" className="w-full aspect-video object-cover" />}
                <div className="p-6"><div className="flex gap-2"><Badge>{POST_CATS[p.category] || p.category}</Badge>{p.source_type === "resmi" && <Badge tone="red">Resmî kaynak</Badge>}</div>
                  <h2 className="mt-3 font-black text-lg">{p.title}</h2><p className="text-sm text-zinc-600 mt-2 line-clamp-3">{p.excerpt}</p><p className="text-xs text-zinc-500 mt-3">{fmtDate(p.published_at)}</p></div>
              </Link>
            ))}
          </div>
        ) : <Empty text="Henüz içerik yayınlanmadı." />}
      </Section>
    </>
  );
}

export function PostDetail({ kind }) {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get(`/posts/${slug}`).then((r) => setP(r.data)).catch(() => setErr(true)); }, [slug]);
  useSeo(p?.seo_title || p?.title, p?.seo_description || p?.excerpt);
  if (err) return <Section><Empty text="İçerik bulunamadı." /></Section>;
  if (!p) return <Loading />;
  return (
    <article>
      <PageHero eyebrow={POST_CATS[p.category] || "Duyuru"} title={p.title} text={fmtDate(p.published_at)} />
      <Section className="max-w-3xl">
        {p.cover && <img src={p.cover} alt={p.title} className="w-full mb-8" />}
        {kind === "guide" && <p className="mb-6"><Badge tone={p.source_type === "resmi" ? "red" : "dark"} testid="post-source-type">{p.source_type === "resmi" ? "Resmî açıklama" : "Kurum bilgilendirmesi"}</Badge></p>}
        <div className="prose-ada" data-testid="post-content">{(p.content || "").split("\n").map((x, i) => <p key={i}>{x}</p>)}</div>
        {(p.source_url || p.source_name) && (
          <div className="mt-8 border-l-4 border-zinc-950 bg-zinc-50 p-4 text-sm" data-testid="post-source">
            <p><b>Kaynak:</b> {p.source_url ? <a href={safeUrl(p.source_url)} target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-1">{p.source_name || p.source_url}<ExternalLink size={12} /></a> : p.source_name}</p>
            {p.source_checked_at && <p className="mt-1"><b>Son kontrol / güncelleme:</b> {fmtDate(p.source_checked_at)}</p>}
          </div>
        )}
        <Link to={kind === "guide" ? "/rehber" : "/duyurular"} className="inline-block mt-8 underline text-sm">← Tüm içerikler</Link>
      </Section>
    </article>
  );
}

function ContactForm() {
  const [f, setF] = useState({ name: "", phone: "", email: "", subject: "", message: "", kvkk_ack: false, website: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);
  const started = useRef(Date.now());
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (!f.kvkk_ack) return setErr("Aydınlatma metnini onaylayın.");
    setBusy(true);
    try { await api.post("/contact", { ...f, started_at: started.current }); setOk(true); } catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  };
  if (ok) return <div className="border-2 border-green-600 bg-green-50 p-6" data-testid="contact-success"><CheckCircle2 className="text-green-700" /><p className="mt-2 font-bold">Mesajınız alındı. En kısa sürede dönüş yapacağız.</p></div>;
  return (
    <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2" data-testid="contact-form">
      <div><label className="label" htmlFor="cf-n">Ad soyad *</label><input id="cf-n" className="field" required value={f.name} onChange={set("name")} data-testid="contact-name" /></div>
      <div><label className="label" htmlFor="cf-p">Telefon</label><input id="cf-p" className="field" type="tel" value={f.phone} onChange={set("phone")} data-testid="contact-phone" /></div>
      <div><label className="label" htmlFor="cf-e">E-posta *</label><input id="cf-e" className="field" type="email" required value={f.email} onChange={set("email")} data-testid="contact-email" /></div>
      <div><label className="label" htmlFor="cf-s">Konu *</label><input id="cf-s" className="field" required value={f.subject} onChange={set("subject")} data-testid="contact-subject" /></div>
      <div className="sm:col-span-2"><label className="label" htmlFor="cf-m">Mesaj *</label><textarea id="cf-m" rows={5} className="field" required minLength={10} value={f.message} onChange={set("message")} data-testid="contact-message" /></div>
      <input type="text" className="hidden" tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} aria-hidden="true" />
      <label className="sm:col-span-2 flex gap-3 text-sm"><input type="checkbox" className="accent-[#dc2626] w-4 h-4 mt-0.5" checked={f.kvkk_ack} onChange={set("kvkk_ack")} data-testid="contact-kvkk" /><span><Link to="/yasal/kvkk-aydinlatma-metni" className="underline">KVKK Aydınlatma Metni</Link>'ni okudum. *</span></label>
      {err && <p className="sm:col-span-2 border-l-4 border-[#dc2626] bg-red-50 text-red-800 px-4 py-3 text-sm" role="alert" data-testid="contact-error">{err}</p>}
      <button className="btn-red sm:col-span-2" disabled={busy} data-testid="contact-submit-btn">{busy && <Loader2 size={16} className="animate-spin" />}Gönder</button>
    </form>
  );
}

export function Contact() {
  const s = useSite();
  useSeo("İletişim ve Ulaşım", "Ada Özel Güvenlik Eğitim Kurumu iletişim: Camikebir Mah., Aran Han İş Merkezi, Kuşadası / Aydın. Tel: 0543 778 75 73");
  const q = encodeURIComponent(s.map_query || s.address || "Kuşadası Aydın");
  return (
    <>
      <PageHero eyebrow="İletişim" title="İletişim ve Ulaşım" />
      <Section>
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="grid gap-3 content-start">
            <div className="bg-zinc-950 text-white p-6 sm:p-8 grid gap-5 text-sm">
              <p className="flex gap-3"><MapPin className="text-[#dc2626] shrink-0" /><span data-testid="contact-address">{s.address}</span></p>
              <a href={`tel:+9${(s.phone || "").replace(/\D/g, "")}`} className="flex gap-3 hover:text-red-300" data-testid="contact-phone-link"><Phone className="text-[#dc2626]" />{s.phone}</a>
              <p className="flex gap-3"><Clock className="text-[#dc2626] shrink-0" /><span>{s.working_hours}</span></p>
              {s.email && <a href={`mailto:${s.email}`} className="flex gap-3 hover:text-red-300" data-testid="contact-email-link"><Mail className="text-[#dc2626]" />{s.email}</a>}
              {s.socials?.instagram && <a href={s.socials.instagram} target="_blank" rel="noreferrer" className="flex gap-3 hover:text-red-300" data-testid="contact-instagram"><Instagram className="text-[#dc2626]" />@adaozelguvenlik</a>}
              {s.socials?.google && <a href={s.socials.google} target="_blank" rel="noreferrer" className="flex gap-3 hover:text-red-300" data-testid="contact-google"><Star className="text-[#dc2626]" />Google'da Görüntüle / Yorum Yap</a>}
              <div className="flex flex-wrap gap-2 pt-2">
                <a href={waLink()} target="_blank" rel="noreferrer" className="btn-red !bg-[#25D366] hover:!bg-[#1da851]" data-testid="contact-whatsapp-btn"><MessageCircle size={16} />WhatsApp</a>
                <a href={`https://www.google.com/maps/dir/?api=1&destination=${q}`} target="_blank" rel="noreferrer" className="btn-outline text-white" data-testid="contact-directions-btn"><Navigation size={16} />Yol Tarifi</a>
              </div>
            </div>
            <iframe title="Ada Özel Güvenlik Eğitim Kurumu harita" src={`https://maps.google.com/maps?q=${q}&z=16&output=embed`} className="w-full h-72 border border-zinc-200" loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-testid="contact-map" />
          </div>
          <div className="card-ada p-6 sm:p-8"><h2 className="font-black text-xl mb-5">Bize yazın</h2><ContactForm /></div>
        </div>
      </Section>
    </>
  );
}

export function LegalList() {
  const [items, setItems] = useState(null);
  useSeo("Kişisel Verilerin Korunması ve Yasal Metinler", "KVKK aydınlatma metni, gizlilik politikası ve çerez politikası.");
  useEffect(() => { api.get("/legal").then((r) => setItems(r.data)); }, []);
  return (
    <>
      <PageHero eyebrow="KVKK" title="Kişisel Verilerin Korunması ve Yasal Metinler" />
      <Section className="max-w-4xl">{!items ? <Loading /> : <div className="grid gap-3">{items.map((l) => <Link key={l.id} to={`/yasal/${l.slug}`} className="card-ada p-5 flex justify-between items-center" data-testid={`legal-${l.slug}`}><span className="font-bold">{l.title}</span>{l.status === "taslak" && <Badge tone="amber">Taslak</Badge>}</Link>)}</div>}</Section>
    </>
  );
}

export function LegalPage() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get(`/legal/${slug}`).then((r) => setP(r.data)).catch(() => setErr(true)); }, [slug]);
  useSeo(p?.title, p?.title);
  if (err) return <Section><Empty text="Metin bulunamadı." /></Section>;
  if (!p) return <Loading />;
  return (
    <>
      <PageHero eyebrow="Yasal Metin" title={p.title} text={`Son güncelleme: ${fmtDate(p.updated_at)}`} />
      <Section className="max-w-3xl">
        {p.status === "taslak" && <p className="mb-6 border-l-4 border-amber-500 bg-amber-50 p-4 text-sm" data-testid="legal-draft-warning">Bu metin taslak niteliğindedir; hukuki kontrol sonrası yayınlanacaktır.</p>}
        <div className="prose-ada" data-testid="legal-content">{p.content.split("\n").map((x, i) => <p key={i}>{x}</p>)}</div>
      </Section>
    </>
  );
}
